"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

/**
 * Rotation slide layers for the dark-funnel hero.
 *
 * The raw CSS `background-image` version of these slides downloaded the
 * full-size JPGs (~360-410KB each) at High priority during initial load,
 * saturating slow connections and pushing LCP past 8s in Lighthouse. The
 * slides are invisible until 5.1s/11.1s into the CSS rotation, so nothing
 * above the fold needs them at load time.
 *
 * This component keeps the exact 3-child DOM shape the
 * `.roma-funnel-hero-slides > div:nth-child(n)` animation CSS expects
 * (slide 1 stays an empty div over the prioritized next/image base layer),
 * but mounts slides 2-3 as optimized next/image renders only after
 * `window` load, taking them off the LCP critical path entirely.
 */
export function RomaHeroRotationSlides({
  images,
}: {
  images: readonly string[];
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (document.readyState === "complete") {
      setReady(true);
      return;
    }
    const onLoad = () => setReady(true);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return (
    <div className="roma-funnel-hero-slides absolute inset-0">
      {images.map((src, index) => (
        <div key={src} className="absolute inset-0 overflow-hidden">
          {index > 0 && ready ? (
            <Image
              src={src}
              alt=""
              fill
              sizes="100vw"
              quality={70}
              className="object-cover object-center"
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
